import { cn } from '@lib/utils';
import { useToastStore, type ToastVariant } from '@stores/toast.store';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Info, X, XCircle, type LucideIcon } from 'lucide-react';
import { useEffect } from 'react';



const ICON: Record<ToastVariant, LucideIcon> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const ICON_BG: Record<ToastVariant, string> = {
  success: 'bg-gradient-primary shadow-btn-green',
  error: 'bg-gradient-danger shadow-[0_4px_16px_rgba(244,63,94,0.32)]',
  warning: 'bg-gradient-warning shadow-[0_4px_16px_rgba(251,191,36,0.32)]',
  info: 'bg-gradient-blue shadow-btn-blue',
};

/**
 * Global toast mount point — put once at the root of App.tsx.
 * Spec rule 8: top-center, glass-morphism, max 1 at a time, 4s auto-dismiss.
 */
export function ToastContainer(): JSX.Element {
  const current = useToastStore((s) => s.current);
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    if (!current) return;
    const duration = current.duration ?? 4000;
    if (duration <= 0) return;
    const t = setTimeout(dismiss, duration);
    return () => clearTimeout(t);
  }, [current, dismiss]);

  return (
    <div className="pointer-events-none fixed left-1/2 top-5 z-[300] -translate-x-1/2">
      <AnimatePresence mode="wait">
        {current ? (
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: -16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              'pointer-events-auto flex min-w-[320px] max-w-[440px] items-start gap-3',
              'rounded-2xl border border-apple-divider glass px-4 py-3.5 shadow-floating',
            )}
            role="status"
            aria-live="polite"
          >
            {(() => {
              const Icon = ICON[current.variant];
              return (
                <span
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white',
                    ICON_BG[current.variant],
                  )}
                >
                  <Icon size={18} strokeWidth={2} />
                </span>
              );
            })()}

            <div className="min-w-0 flex-1 pt-0.5">
              <div className="text-[14px] font-semibold text-apple-label leading-snug">
                {current.title}
              </div>
              {current.message ? (
                <div className="mt-0.5 text-[13px] text-apple-gray-600 leading-snug">
                  {current.message}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={dismiss}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-apple-gray-500 transition hover:bg-apple-gray-200 hover:text-apple-label"
              aria-label="Dismiss"
            >
              <X size={14} strokeWidth={2} />
            </button>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
